import { AIAssistantStatus } from '@/types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertCircle, CheckCircle2, TestTube2 } from 'lucide-react';

interface StatusConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentStatus: AIAssistantStatus;
  newStatus: AIAssistantStatus;
  onConfirm: () => void;
  isLoading?: boolean;
}

const STATUS_CONFIG = {
  [AIAssistantStatus.ACTIVE]: {
    icon: CheckCircle2,
    iconColor: 'text-green-600',
    title: 'Ativar Assistente',
    description: 'O assistente começará a processar mensagens automaticamente. As mensagens recebidas pelo webhook serão enviadas para o agente de IA e as respostas serão retornadas.',
  },
  [AIAssistantStatus.INACTIVE]: {
    icon: AlertCircle,
    iconColor: 'text-red-600',
    title: 'Desativar Assistente',
    description: 'O assistente parará de processar mensagens. Webhooks recebidos serão ignorados e nenhuma resposta automática será enviada.',
  },
  [AIAssistantStatus.TEST]: {
    icon: TestTube2,
    iconColor: 'text-yellow-600',
    title: 'Ativar Modo de Teste',
    description: 'O assistente funcionará apenas para os números de telefone autorizados configurados. Use este modo para testar o comportamento antes de ativar para todos os usuários.',
  },
};

export function StatusConfirmationDialog({
  open,
  onOpenChange,
  currentStatus,
  newStatus,
  onConfirm,
  isLoading = false,
}: StatusConfirmationDialogProps) {
  const config = STATUS_CONFIG[newStatus];
  const Icon = config.icon;

  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-full bg-muted ${config.iconColor}`}>
              <Icon className="h-5 w-5" />
            </div>
            <AlertDialogTitle className="text-xl">{config.title}</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-base leading-relaxed pt-2">
            {config.description}
          </AlertDialogDescription>

          {currentStatus !== AIAssistantStatus.INACTIVE && newStatus !== AIAssistantStatus.INACTIVE && (
            <div className="mt-4 p-3 rounded-md bg-muted/50 text-sm text-muted-foreground">
              Status atual: <span className="font-medium">{getStatusLabel(currentStatus)}</span>
            </div>
          )}
        </AlertDialogHeader>

        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel disabled={isLoading}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            className={getButtonClassName(newStatus)}
          >
            {isLoading ? 'Processando...' : 'Confirmar'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function getStatusLabel(status: AIAssistantStatus): string {
  const labels = {
    [AIAssistantStatus.ACTIVE]: 'Ativo',
    [AIAssistantStatus.INACTIVE]: 'Inativo',
    [AIAssistantStatus.TEST]: 'Teste',
  };
  return labels[status];
}

function getButtonClassName(status: AIAssistantStatus): string {
  const classes = {
    [AIAssistantStatus.ACTIVE]: 'bg-green-600 hover:bg-green-700',
    [AIAssistantStatus.INACTIVE]: 'bg-red-600 hover:bg-red-700',
    [AIAssistantStatus.TEST]: 'bg-yellow-600 hover:bg-yellow-700',
  };
  return classes[status];
}
